/**
 * Wakes the device location provider early so the login geofence check does not
 * have to wait for a cold fix.
 *
 * WHY THIS EXISTS (measured 2026-09-14, Galaxy S24+ / Android 16 / WebView 152,
 * against the shipped app):
 *
 *   first requests after the provider had been idle   14.0s, 10.4s, 29.9s
 *   every request once the provider was warm          1ms - 48ms
 *
 * The same spread appeared through the native Capacitor plugin (6.7s cold,
 * 29.9s, then milliseconds), so this is the Android location subsystem spinning
 * up, not the WebView bridge, not `maximumAge: 0`, and not the vendor. Warmth
 * proved to be system-level rather than per-process: after a force-stop and
 * relaunch, a fresh request still returned in 48ms.
 *
 * That last detail is what makes this worth doing. `softCheck()` acquires its
 * own position and we cannot pass it options or a cached fix, but we do not
 * need to: if the provider is already awake when the vendor asks, the vendor
 * gets its answer in milliseconds regardless of how it asks.
 *
 * Without this, a login-time geofence check inherits a 10-30 second blocking
 * wait on the one path where the player is staring at a spinner.
 *
 * WHAT THIS DOES NOT FIX. The acquisition it triggers itself takes up to ~30s,
 * so a player who reaches the geofence check sooner than that still waits,
 * though less: the check joins the acquisition already in flight rather than
 * starting its own. It fully pays off only when app start leads the check by
 * roughly that much, which covers a typed or one-time-code login but not an
 * instant auto-login from a stored session.
 *
 * The stronger version is `watchPosition`, which holds the location session
 * open so every later request is immediate, cleared once the check is done.
 * That is a continuous-GPS battery cost on a client with a known thermal
 * history, so it is deliberately left as a decision rather than assumed.
 */
import { Capacitor } from '@capacitor/core';

/**
 * Whether location permission is ALREADY granted, asked in a way that cannot
 * itself raise a prompt.
 *
 * This gate is the whole design. Warming means requesting a position, and
 * requesting a position from an ungranted app raises the system permission
 * dialog. Doing that at app start would move the prompt away from the moment
 * the player is told why we need it, which is both worse UX and a good way to
 * train people to deny it. First-time players therefore pay the cold fix once,
 * at login, where the prompt belongs; returning players get a warm provider.
 *
 * ON NATIVE, `navigator.permissions` IS THE WRONG SOURCE. Measured 2026-09-14
 * on a Galaxy S24+ with the app holding ACCESS_FINE_LOCATION granted=true:
 *
 *   navigator.permissions.query({name:'geolocation'})  ->  "prompt"   (wrong)
 *   Geolocation.checkPermissions()                     ->  "granted"  (right)
 *
 * The Permissions API reports the WebView origin's own permission, which
 * Capacitor grants per request through BridgeWebChromeClient rather than
 * persisting, so it reads "prompt" forever no matter what the app holds. An
 * earlier version of this file gated on it and was therefore dead code on
 * Android: it never warmed anything. Neither call prompts, so the plugin check
 * is a safe swap.
 */
async function hasLocationPermission(): Promise<boolean> {
  try {
    if (Capacitor.isNativePlatform()) {
      const { Geolocation } = await import('@capacitor/geolocation');
      const status = await Geolocation.checkPermissions();
      return status.location === 'granted' || status.coarseLocation === 'granted';
    }
    const status = await navigator.permissions?.query({
      name: 'geolocation' as PermissionName
    });
    return status?.state === 'granted';
  } catch {
    // Missing or throwing API. Assume not granted: a needless skip costs one
    // slow login, a needless prompt costs trust.
    return false;
  }
}

/**
 * Fire-and-forget. Never prompts, never throws, never blocks a caller.
 *
 * The result is deliberately discarded. This is not a location check and its
 * position is never used for gating: it exists only so the provider is awake.
 *
 * `maximumAge: 0` IS LOAD-BEARING AND MUST NOT BE RELAXED. It looks wasteful
 * here, since we throw the position away and a cached one would do. It is the
 * opposite. Measured on the device:
 *
 *   warm with maximumAge: 600000  ->  returns in 40ms from cache,
 *                                     then the next real check still took 30.0s
 *   warm with maximumAge: 0       ->  actually acquires a fix, and subsequent
 *                                     checks return in tens of milliseconds
 *
 * A permissive `maximumAge` is served straight from cache without ever starting
 * the location engine, so it warms nothing. The first version of this file did
 * exactly that and was a no-op. Forcing a real acquisition is the entire point:
 * the cost is paid here, in the background, instead of at login in front of the
 * player.
 *
 * Note this is a different use of `maximumAge: 0` from the one in
 * GEOLOCATION_OPTIONS. There it is regulatory, forbidding a stale position from
 * satisfying a compliance check. Here it is purely mechanical.
 */
export function warmLocationProvider(): void {
  void (async () => {
    try {
      if (!(await hasLocationPermission())) return;

      const options = { enableHighAccuracy: false, timeout: 60_000, maximumAge: 0 };

      // Warm through the SAME API the permission was checked against.
      //
      // This previously checked the Capacitor plugin on native and then called
      // `navigator.geolocation`, which is a different permission surface --
      // documented a few lines up as reporting "prompt" on Android however the
      // app is actually provisioned. Checking one and calling the other means
      // the gate authorises nothing about the call: `navigator.geolocation` in
      // the WebView raises the runtime prompt through Capacitor's
      // BridgeWebChromeClient (measured on-device), so this could surface a
      // permission dialog at app START rather than at login, which is precisely
      // the contract this function claims to keep.
      if (Capacitor.isNativePlatform()) {
        const { Geolocation } = await import('@capacitor/geolocation');
        await Geolocation.getCurrentPosition(options);
        return;
      }

      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(() => undefined, () => undefined, options);
    } catch {
      /* warming is best-effort by definition */
    }
  })();
}
